import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { AuthSessionData, AuthUser } from '@/modules/auth/constants/auth.schemas'
import type { Profile } from '@/modules/profile/constants/profile.schemas'

const SECONDS_TO_MS = 1000

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export type AuthProfileSnapshot = Pick<Profile, 'id' | 'name' | 'email' | 'phone' | 'cpf'>

interface AuthStore {
  expiresAt: number | null
  status: AuthStatus
  user: AuthUser | null
  accessToken: string | null
  setLoading: () => void
  setSession: (session: AuthSessionData) => void
  syncProfile: (profile: AuthProfileSnapshot) => void
  clearSession: () => void
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      status: 'loading',
      user: null,
      accessToken: null,
      expiresAt: null,
      setLoading: () => set({ status: 'loading' }),
      // Session payloads (register, login, refresh) only carry id and name; contact data comes
      // from GET /v1/profile via syncProfile and must survive token refreshes for the same user.
      setSession: (session) =>
        set((state) => {
          const sameUser = state.user?.id === session.user_id
          // Refresh responses carry the name from login time; keep the one synced from the profile.
          const name = sameUser && state.user ? state.user.name : session.user_name

          return {
            status: 'authenticated',
            accessToken: session.access_token,
            expiresAt: Date.now() + session.expires_in * SECONDS_TO_MS,
            user: {
              id: session.user_id,
              name,
              initials: getInitials(name),
              email: sameUser ? state.user?.email : undefined,
              phone: sameUser ? state.user?.phone : undefined,
              cpf: sameUser ? state.user?.cpf : undefined,
              onboardingRequired: session.onboarding_required,
            },
          }
        }),
      syncProfile: (profile) =>
        set((state) => {
          if (!state.user || state.user.id !== profile.id) {
            return {}
          }

          const name = profile.name.trim() || state.user.name

          return {
            user: {
              ...state.user,
              name,
              initials: getInitials(name),
              email: profile.email ?? undefined,
              phone: profile.phone ?? undefined,
              cpf: profile.cpf ?? undefined,
            },
          }
        }),
      clearSession: () =>
        set({ status: 'anonymous', user: null, accessToken: null, expiresAt: null }),
    }),
    { name: 'partiu-quadra-auth-store' },
  ),
)

function getInitials(name: string): string {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  return initials || 'PQ'
}
